import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { brokerRedirectUrl, track } from '../lib/api';
import { trackPixelEvent } from '../lib/pixels';
import { ShieldCheck, Zap, Target, Crown, PhoneCall } from 'lucide-react';
import HeroVideo from './HeroVideo';

export default function Hero({ settings, geo, recommendedKey, stats }) {
  const tg = settings?.telegram_link || '#';
  const elite = settings?.elite_apply_link || tg;
  const kicker = settings?.hero_kicker || 'INTRODUCING ALVEETRADE';
  const title = settings?.hero_title || 'A MASSIVE UPGRADE.';
  const subtitle = settings?.hero_subtitle || 'The modern financial system is designed to keep you dependent. AlveeTrade was built for people who see through it.';
  const ctaLabel = settings?.cta_primary_label || 'Join Free Telegram Community';
  const eliteCta = settings?.cta_secondary_label || 'Apply for Elite';

  function handleJoin(source, url) {
    trackPixelEvent('Subscribe', { source });
    track({ type: 'tg_click', country_code: geo?.country_code, country_name: geo?.country_name, source });
  }

  const joined = stats?.total_joined || settings?.joined_base || 131312;
  const formatted = joined.toLocaleString();
  const countryLabel = geo?.country_name ? `Detected: ${geo.country_name}` : 'Global access';

  return (
    <section id="top" className="relative overflow-hidden bg-[hsl(0_0%_4%)] hero-glow noise-overlay">
      <div className="container-x pt-6 sm:pt-10 pb-8 sm:pb-12">
        <div className="grid gap-6 lg:gap-10 lg:grid-cols-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-7 space-y-4 relative z-10 order-2 lg:order-1"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)]">{kicker}</span>
              <span className="h-px w-10 bg-[hsl(43_92%_56%)]/60" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2"><span className="pulse-dot" /> {countryLabel}</span>
            </div>
            <h1 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.92] tracking-tight">
              {title}
            </h1>
            <p className="text-sm md:text-base text-[hsl(0_0%_82%)] max-w-xl leading-relaxed">{subtitle}</p>
            <div className="flex flex-wrap items-center gap-3">
              <a href={tg} target="_blank" rel="noreferrer" onClick={() => handleJoin('hero_free')} data-testid="hero-join-telegram-button">
                <Button className="cta-sheen border-0 text-white font-semibold h-11 px-5 text-sm sm:text-base">
                  {ctaLabel}
                </Button>
              </a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} data-testid="hero-elite-button">
                <Button variant="ghost" className="h-11 px-4 text-white border border-[rgba(245,185,66,0.45)] hover:bg-[rgba(245,185,66,0.1)] gap-2 text-sm">
                  <Crown className="h-4 w-4 text-[hsl(43_92%_56%)]" /> {eliteCta}
                </Button>
              </a>
              <Link to="/personal-call" data-testid="hero-personal-call-button">
                <Button variant="ghost" className="h-11 px-4 text-white border border-[rgba(140,90,255,0.45)] hover:bg-[rgba(140,90,255,0.1)] gap-2 text-sm">
                  <PhoneCall className="h-4 w-4 text-[hsl(280_70%_75%)]" /> 1-on-1 Personal Call
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-3 flex-wrap pt-1">
              <div className="text-xs sm:text-sm text-muted-foreground">
                <span className="text-white font-semibold">{formatted}+</span> traders already joined
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="border-[rgba(245,185,66,0.35)] text-[hsl(43_92%_56%)] gap-1.5 text-[10px]"><Zap className="h-3 w-3" /> Live Signals</Badge>
                <Badge variant="outline" className="border-[rgba(245,185,66,0.35)] text-[hsl(43_92%_56%)] gap-1.5 text-[10px]"><ShieldCheck className="h-3 w-3" /> Risk First</Badge>
                <Badge variant="outline" className="border-[rgba(245,185,66,0.35)] text-[hsl(43_92%_56%)] gap-1.5 text-[10px]"><Target className="h-3 w-3" /> Daily Breakdowns</Badge>
              </div>
            </div>
            {(() => {
              const pills = [];
              if (settings?.broker_enabled && settings?.broker_link) {
                pills.push({
                  key: 'po',
                  name: settings.broker_name || 'Pocket Option',
                  href: brokerRedirectUrl('hero_pill', 'po'),
                  testid: 'hero-broker-pill',
                  bgClass: 'bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] border border-[hsl(43_92%_42%)]',
                  shadow: [
                    '0 4px 18px -4px rgba(245,185,66,0.45)',
                    '0 8px 28px -4px rgba(245,185,66,0.85)',
                    '0 4px 18px -4px rgba(245,185,66,0.45)',
                  ],
                  source: 'hero_pill',
                });
              }
              if (settings?.quotex_enabled && settings?.quotex_broker_link) {
                pills.push({
                  key: 'quotex',
                  name: settings.quotex_broker_name || 'Quotex',
                  href: brokerRedirectUrl('hero_pill_quotex', 'quotex'),
                  testid: 'hero-broker-pill-quotex',
                  bgClass: 'bg-[hsl(212_80%_70%)] hover:bg-[hsl(212_80%_64%)] border border-[hsl(212_70%_52%)]',
                  shadow: [
                    '0 4px 18px -4px rgba(108,176,255,0.45)',
                    '0 8px 28px -4px rgba(108,176,255,0.85)',
                    '0 4px 18px -4px rgba(108,176,255,0.45)',
                  ],
                  source: 'hero_pill_quotex',
                });
              }
              if (pills.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-2 self-start">
                  {pills.map((p) => (
                    <motion.a
                      key={p.key}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={() => track({ type: 'broker_click', country_code: geo?.country_code, country_name: geo?.country_name, source: p.source })}
                      data-testid={p.testid}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{
                        opacity: 1,
                        y: [0, -3, 0],
                        boxShadow: p.shadow,
                      }}
                      transition={{
                        opacity: { duration: 0.5, delay: 0.25 },
                        y: { duration: 2.6, ease: 'easeInOut', repeat: Infinity, delay: 0.4 },
                        boxShadow: { duration: 2.6, ease: 'easeInOut', repeat: Infinity, delay: 0.4 },
                      }}
                      whileHover={{ scale: 1.035, y: -4 }}
                      whileTap={{ scale: 0.97 }}
                      className={'group relative inline-flex items-center gap-2 px-3.5 py-2 rounded-full overflow-hidden text-[11px] uppercase tracking-[0.16em] ' + p.bgClass}
                    >
                      {/* Animated shimmer sweep */}
                      <motion.span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 -inset-x-4"
                        style={{
                          background:
                            'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                        }}
                        animate={{ x: ['-110%', '120%'] }}
                        transition={{ duration: 2.4, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.4 }}
                      />
                      <span className="relative h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
                      <span className="relative text-black font-bold">Recommended broker</span>
                      <span className="relative h-3 w-px bg-black/30" aria-hidden="true" />
                      <span className="relative text-black font-bold">{p.name}</span>
                      <motion.span
                        className="relative text-black/70 font-medium tracking-[0.14em] inline-flex items-center gap-0.5"
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 1.6, ease: 'easeInOut', repeat: Infinity, delay: 0.2 }}
                      >
                        → Sign up free
                      </motion.span>
                    </motion.a>
                  ))}
                </div>
              );
            })()}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5 relative z-10 order-1 lg:order-2"
          >
            <HeroVideo
              platform={settings?.hero_video_platform || 'youtube'}
              url={settings?.hero_video_url || ''}
              duration={settings?.hero_video_duration || ''}
            />
            <div className="hidden sm:block absolute -top-6 -right-6 h-24 w-24 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(245,185,66,0.4), transparent 60%)' }} />
            <div className="hidden sm:block absolute -bottom-6 -left-6 h-28 w-28 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(225,29,46,0.32), transparent 60%)' }} />
          </motion.div>
        </div>
        <div data-recommended-key={recommendedKey} className="sr-only" aria-hidden="true" />
      </div>
    </section>
  );
}
